// components/ui/StatusBadge.tsx
import React from 'react'
import { LucideIcon } from 'lucide-react'
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Ban,
  RotateCcw,
  Package,
  Truck,
  DollarSign,
  CreditCard,
  Gift,
  Wallet,
  User,
  Home,
  Coffee,
  Utensils
} from 'lucide-react'

type StatusColor = 'gray' | 'blue' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'pink' | 'cyan'

interface StatusBadgeProps {
  status: string
  label?: string
  color?: StatusColor
  icon?: LucideIcon
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'subtle'
  showIcon?: boolean
  className?: string
  variants?: Record<string, { color: StatusColor; label: string; icon?: LucideIcon }>
}

const sizeClasses = {
  xs: 'px-1.5 py-0.5 text-xs',
  sm: 'px-2 py-1 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base'
}

const colorClasses: Record<StatusColor, string> = {
  gray: 'bg-gray-100 text-gray-800 border-gray-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  green: 'bg-green-100 text-green-800 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  pink: 'bg-pink-100 text-pink-800 border-pink-200',
  cyan: 'bg-cyan-100 text-cyan-800 border-cyan-200'
}

const darkColorClasses: Record<StatusColor, string> = {
  gray: 'bg-gray-900/30 text-gray-300 border-gray-700',
  blue: 'bg-blue-900/30 text-blue-300 border-blue-700',
  green: 'bg-green-900/30 text-green-300 border-green-700',
  yellow: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  orange: 'bg-orange-900/30 text-orange-300 border-orange-700',
  red: 'bg-red-900/30 text-red-300 border-red-700',
  purple: 'bg-purple-900/30 text-purple-300 border-purple-700',
  pink: 'bg-pink-900/30 text-pink-300 border-pink-700',
  cyan: 'bg-cyan-900/30 text-cyan-300 border-cyan-700'
}

const outlineColorClasses: Record<StatusColor, string> = {
  gray: 'border border-gray-500 text-gray-500',
  blue: 'border border-blue-500 text-blue-500',
  green: 'border border-green-500 text-green-500',
  yellow: 'border border-yellow-500 text-yellow-500',
  orange: 'border border-orange-500 text-orange-500',
  red: 'border border-red-500 text-red-500',
  purple: 'border border-purple-500 text-purple-500',
  pink: 'border border-pink-500 text-pink-500',
  cyan: 'border border-cyan-500 text-cyan-500'
}

const subtleColorClasses: Record<StatusColor, string> = {
  gray: 'bg-gray-50 text-gray-600',
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-green-50 text-green-600',
  yellow: 'bg-yellow-50 text-yellow-600',
  orange: 'bg-orange-50 text-orange-600',
  red: 'bg-red-50 text-red-600',
  purple: 'bg-purple-50 text-purple-600',
  pink: 'bg-pink-50 text-pink-600',
  cyan: 'bg-cyan-50 text-cyan-600'
}

const variantClasses = {
  default: 'border',
  outline: 'border-2 bg-transparent',
  subtle: 'border-0'
}

const getStatusIcon = (status: string): LucideIcon | undefined => {
  const iconMap: Record<string, LucideIcon> = {
    completed: CheckCircle,
    confirmed: CheckCircle,
    paid: CheckCircle,
    delivered: CheckCircle,
    ready: CheckCircle,
    served: CheckCircle,
    success: CheckCircle,

    pending: Clock,
    waiting: Clock,
    open: Clock,
    preparing: Clock,
    in_progress: Clock,

    draft: AlertCircle,
    parked: AlertCircle,
    partial: AlertCircle,
    assigned: AlertCircle,
    in_transit: AlertCircle,

    voided: Ban,
    canceled: Ban,
    failed: Ban,
    unpaid: Ban,

    refunded: RotateCcw,
    exchanged: RotateCcw,
    returned: RotateCcw,

    processing: Package,
    shipped: Truck,

    cash: DollarSign,
    card: CreditCard,
    credit: Wallet,
    giftcard: Gift,

    customer: User,
    waiter: User,
    driver: User,

    dine_in: Home,
    takeout: Package,
    delivery: Truck,

    kitchen: Utensils,
    bar: Coffee
  }

  return iconMap[status.toLowerCase()]
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  color,
  icon: IconProp,
  size = 'sm',
  variant = 'default',
  showIcon = true,
  className = '',
  variants
}) => {
  // Determine color and label from variants or props
  let badgeColor: StatusColor = color || 'gray'
  let badgeLabel = label || status
  let Icon = IconProp

  if (variants && variants[status]) {
    const variantConfig = variants[status]
    badgeColor = variantConfig.color
    badgeLabel = variantConfig.label || badgeLabel
    if (!Icon && variantConfig.icon) {
      Icon = variantConfig.icon
    }
  }

  // If no icon specified but showIcon is true, try to get one
  if (showIcon && !Icon) {
    Icon = getStatusIcon(status)
  }

  // Get color classes based on variant
  let colorClass = colorClasses[badgeColor]
  if (variant === 'outline') {
    colorClass = outlineColorClasses[badgeColor]
  } else if (variant === 'subtle') {
    colorClass = subtleColorClasses[badgeColor]
  }

  // For dark mode, use dark classes (assuming dark mode is default in your app)
  const darkMode = true // You can make this dynamic if needed
  if (darkMode && variant === 'default') {
    colorClass = darkColorClasses[badgeColor]
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${colorClass}
        ${className}
      `}
    >
      {Icon && <Icon size={size === 'xs' ? 12 : size === 'lg' ? 16 : 14} />}
      <span className="whitespace-nowrap">{badgeLabel}</span>
    </span>
  )
}

// Shorthand components for common statuses
export const SuccessBadge: React.FC<Omit<StatusBadgeProps, 'status' | 'color'>> = (props) => (
  <StatusBadge status="success" color="green" {...props} />
)

export const WarningBadge: React.FC<Omit<StatusBadgeProps, 'status' | 'color'>> = (props) => (
  <StatusBadge status="warning" color="yellow" {...props} />
)

export const ErrorBadge: React.FC<Omit<StatusBadgeProps, 'status' | 'color'>> = (props) => (
  <StatusBadge status="error" color="red" {...props} />
)

export const InfoBadge: React.FC<Omit<StatusBadgeProps, 'status' | 'color'>> = (props) => (
  <StatusBadge status="info" color="blue" {...props} />
)